import React, { useEffect, useState } from "react";
import { Ticket, Check, Share2, Copy, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function JoinTheClub() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    consent: true,
    referred_by: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim().toUpperCase();
    if (ref) {
      setForm((f) => ({ ...f, referred_by: ref }));
      api
        .get(`/referral/validate?code=${encodeURIComponent(ref)}`)
        .then((r) => {
          if (r.data.valid) setReferrerInfo(r.data);
        })
        .catch(() => {});
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const { data } = await api.post("/subscribers", form);
      setVoucher({ code: data.voucher_code, offer: data.voucher_offer });
      setStatus("sent");
      setForm({ name: "", phone: "", email: "", address: "", consent: true, referred_by: "" });
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || err.message || "Something went wrong.");
    }
  };

  const onChange = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const shareLink = voucher
    ? `${window.location.origin}/?ref=${encodeURIComponent(voucher.code)}`
    : "";

  const shareMessage = voucher
    ? `I just joined the Naami Kitchens Club — twenty brands under one roof in Edinburgh. Use my code to join too and get invited to our official launch + exclusive perks: ${shareLink}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* noop */
    }
  };

  return (
    <section id="club" className="py-20 lg:py-28" data-testid="club-section">
      <div className="container-nk">
        <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden p-8 md:p-14 grain">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--gold)] opacity-10 blur-3xl pointer-events-none" aria-hidden />

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative z-10">
            <div>
              <p className="eyebrow flex items-center gap-2">
                <Ticket size={13} className="text-gold" />
                // {referrerInfo ? `${referrerInfo.referrer_first_name} invited you to the Naami Club` : "Join the Naami Club"}
              </p>
              <h2 className="font-display text-[48px] md:text-[68px] leading-[0.95] mt-3">
                Join the <span className="text-gold">Naami</span><br />
                Club.
              </h2>
              <p className="mt-6 text-[15.5px] leading-[1.75] text-[var(--text)]/80 font-light max-w-md">
                Sign up to be invited to our official launch, unlock exclusive
                discounts, and get first dibs on new brands as they open under
                our roof.
              </p>
              <p className="mt-4 text-[13.5px] leading-relaxed text-[var(--text)]/65 font-light max-w-md">
                <span className="text-gold font-mono uppercase tracking-[0.16em] text-[10px]">Refer a friend</span> · Share your member code so your friends get the same launch invite — and we'll send you a bonus perk when they sign up.
              </p>
            </div>

            {status === "sent" ? (
              <div className="bg-[var(--surface-elevated)] border border-[var(--border)] rounded-xl p-8 text-center" data-testid="club-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--gold)] flex items-center justify-center mb-5">
                  <Check size={24} className="text-[var(--bg)]" />
                </div>
                <h3 className="font-display text-[32px] leading-none">You're in.</h3>
                <p className="text-muted text-[14px] mt-3 max-w-xs mx-auto">
                  Welcome to the Naami Club. Save your member code — we'll be
                  in touch with launch details and exclusive perks soon.
                </p>

                <div className="mt-5 mx-auto max-w-[320px] rounded-xl border-2 border-dashed border-[var(--gold)] bg-[var(--gold)]/10 p-5" data-testid="club-voucher">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    Your Naami Club Code
                  </p>
                  <p className="font-display text-[36px] tracking-wider text-gold mt-2 select-all" data-testid="club-voucher-code">
                    {voucher?.code || "—"}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted mt-2">
                    Keep this safe · We'll reference it when we get in touch
                  </p>
                </div>

                <div className="mt-7 pt-6 border-t border-[var(--border)]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-2 justify-center">
                    <Share2 size={12} /> Refer a Friend · Exclusive Perks for Both
                  </p>
                  <div className="flex gap-2 mt-4 max-w-[300px] mx-auto">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="plat-btn"
                      style={{ background: "#25D366", color: "#022", padding: "10px 14px", fontSize: "11px", flex: 1 }}
                      data-testid="club-share-whatsapp"
                    >
                      <MessageCircle size={12} style={{ marginRight: 4 }} />
                      WhatsApp
                    </a>
                    <button
                      onClick={copyLink}
                      className="plat-btn"
                      style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)", padding: "10px 14px", fontSize: "11px", flex: 1 }}
                      data-testid="club-share-copy"
                    >
                      <Copy size={12} style={{ marginRight: 4 }} />
                      {copied ? "Copied!" : "Copy link"}
                    </button>
                  </div>
                </div>

                <button className="btn-ghost mt-6" onClick={() => { setStatus("idle"); setVoucher(null); }} data-testid="club-add-another">
                  Sign up another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-4" data-testid="club-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Name *
                    </label>
                    <input required value={form.name} onChange={onChange("name")} className="input-nk" data-testid="club-name" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Phone *
                    </label>
                    <input required value={form.phone} onChange={onChange("phone")} className="input-nk" placeholder="07…" data-testid="club-phone" />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                    Email (optional)
                  </label>
                  <input type="email" value={form.email} onChange={onChange("email")} className="input-nk" data-testid="club-email" />
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                    Referral code (optional)
                  </label>
                  <input
                    value={form.referred_by}
                    onChange={(e) => setForm({ ...form, referred_by: e.target.value.toUpperCase() })}
                    className="input-nk"
                    placeholder="NAAMI-XXXXX"
                    data-testid="club-referred-by"
                  />
                </div>

                <label className="flex items-start gap-2.5 text-[12px] text-muted cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={onChange("consent")}
                    className="mt-0.5"
                    data-testid="club-consent"
                  />
                  <span>
                    I'm happy to receive launch invites, exclusive deals and
                    updates from Naami Kitchens. You can unsubscribe any time.
                  </span>
                </label>

                {error && <p className="text-[var(--error)] text-[13px]" data-testid="club-error">{error}</p>}

                <button
                  type="submit"
                  disabled={status === "sending" || !form.consent}
                  className="btn-primary justify-center mt-2"
                  data-testid="club-submit"
                >
                  {status === "sending" ? "Joining…" : "Join the Club →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
